import { z } from 'zod'

import i18n from '../i18n'
import { formatPhoneNumber, isValidPhoneNumber } from '../utils'

// Ref: https://github.com/angular/angular/blob/87b8aba238ac92f4dbc02dd66be187fc863bdba7/packages/forms/src/validators.ts#LL125C1-L126C1
const emailRegex =
  /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

const baseRegistrationShape = {
  name: z.string().min(1, { message: i18n.t('validations.required') }),
  kana: z.string().optional(),
  phone: z
    .string()
    .refine((v) => isValidPhoneNumber(v), {
      message: i18n.t('validations.phone'),
    })
    .transform((v) => formatPhoneNumber(v)),
  postalCode: z.string().min(1, { message: i18n.t('validations.required') }),
  prefecture: z.string().min(1, { message: i18n.t('validations.required') }),
  city: z.string().min(1, { message: i18n.t('validations.required') }),
  street: z.string().min(1, { message: i18n.t('validations.required') }),
  email: z.string().regex(emailRegex, i18n.t('validations.email')),
  specialServiceRequest: z
    .string()
    .max(200, { message: i18n.t('validations.maxlength', { maximum: 200 }) })
    .optional(),
  companyName: z
    .string()
    .max(255, { message: i18n.t('validations.maxlength', { maximum: 255 }) })
    .optional(),
  purposeOfUse: z
    .string()
    .max(255, { message: i18n.t('validations.maxlength', { maximum: 255 }) })
    .optional(),
  checkInTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, i18n.t('validations.time'))
    .optional()
    .or(z.literal('')),
  // Only validated for invoice-billed plans (see requiredInvoiceShape). Other
  // plans never render or send this field, but react-hook-form keeps the value
  // restored from localStorage, so it must never block their submit.
  invoiceAddressee: z.string().optional(),
  birthYear: z.string().regex(/^\d{4}$/, i18n.t('validations.number')),
  birthMonth: z.string().regex(/^\d{2}$/, i18n.t('validations.number')),
  birthDay: z.string().regex(/^\d{2}$/, i18n.t('validations.number')),
}

const requiredCardShape = {
  cardNumber: z
    .string()
    .refine((v) => v.replaceAll(' ', '').match(/^\d{14,16}$/), {
      message: i18n.t('validations.cardNumber'),
    })
    .transform((v) => v.replaceAll(' ', '')),
  holderName: z.string().min(1, { message: i18n.t('validations.required') }),
  expirationMonth: z
    .string()
    .min(1, { message: i18n.t('validations.required') }),
  expirationYear: z
    .string()
    .min(1, { message: i18n.t('validations.required') }),
  securityCode: z
    .string()
    .regex(/^[0-9]+$/, i18n.t('validations.number'))
    .min(1, { message: i18n.t('validations.required') })
    .max(4, { message: i18n.t('validations.maxlength', { maximum: 4 }) }),
}

// On-site (現地決済) plans never read or send card fields, but react-hook-form
// retains values for unmounted inputs (e.g. a partial number typed while the
// price was still loading, or a card restored from localStorage defaults).
// Validate nothing here so a stale/partial card value can never block submit
// for a field the guest can no longer see. The on-site submit path drops these
// values entirely (no gmoToken is built), so accepting any string is safe.
const optionalCardShape = {
  cardNumber: z.string().optional(),
  holderName: z.string().optional(),
  expirationMonth: z.string().optional(),
  expirationYear: z.string().optional(),
  securityCode: z.string().optional(),
}

// Invoice-billed (請求書払い) plans must tell the property whom to address the
// invoice to; the backend rejects a blank value with 400 (SB-3034).
const requiredInvoiceShape = {
  invoiceAddressee: z
    .string()
    .refine((v) => v.trim().length > 0, {
      message: i18n.t('validations.required'),
    })
    .transform((v) => v.trim())
    .refine((v) => v.length <= 255, {
      message: i18n.t('validations.maxlength', { maximum: 255 }),
    }),
}

type RegistrationSchemaOptions = {
  requiresPayment: boolean
  isInvoicePayment: boolean
}

export const makeRegistrationSchema = ({
  requiresPayment,
  isInvoicePayment,
}: RegistrationSchemaOptions) =>
  z.object({
    ...baseRegistrationShape,
    ...(requiresPayment ? requiredCardShape : optionalCardShape),
    ...(isInvoicePayment ? requiredInvoiceShape : {}),
  })

// registrationSchema is always the required-card variant; type is derived from
// this concrete definition so Registration keeps non-optional card fields.
export const registrationSchema = z.object({
  ...baseRegistrationShape,
  ...requiredCardShape,
})

export type Registration = z.infer<typeof registrationSchema>

export const discountCodeSchema = z.object({
  discountCode: z.string().optional(),
})

export const agreementSchema = z.object({
  agreement: z.boolean(),
})

export type DiscountCode = z.infer<typeof discountCodeSchema>

export type Agreement = z.infer<typeof agreementSchema>
